import type { ReactNode } from 'react';
import type { ICascaderSelectChangeContext, ICascaderSelectOption } from './index';

export type TFormatMessage = (message: { id: string }) => string;

export interface IFindOptionResult {
  option?: ICascaderSelectOption;
  parentOption?: ICascaderSelectOption;
  path: ICascaderSelectOption[];
}

interface IGetColumnTitleParams {
  formatMessage: TFormatMessage;
  columnIndex: number;
  levelTitles?: Array<string | ReactNode>;
  firstLevelTitle: string | ReactNode;
  secondLevelTitle: string | ReactNode;
  locale?: string;
}

export const EMPTY_CONTEXT: ICascaderSelectChangeContext = {
  path: [],
};

export const CASCADER_SELECT_LOCALE_KEYS = {
  placeholder: 'dbh_cascader_select_placeholder',
  emptyText: 'dbh_spotlight_no_data',
  searchPlaceholder: 'dbh_search',
  firstLevelTitle: 'dbh_cascader_select_first_level_title',
  secondLevelTitle: 'dbh_cascader_select_second_level_title',
  overallTitle: 'dbh_cascader_select_overall_title',
};

export function formatLocaleText(
  formatMessage: TFormatMessage,
  text?: string | ReactNode,
  fallback?: string,
): string | ReactNode {
  if (!text) {
    return text;
  }

  if (typeof text !== 'string') {
    return text;
  }

  const message = formatMessage({ id: text });

  if (message && message !== text) {
    return message;
  }

  return fallback || text;
}

export function getTextContent(text?: string | ReactNode): string {
  if (typeof text === 'string') {
    return text;
  }

  if (typeof text === 'number') {
    return String(text);
  }

  return '';
}

export function getOptionDisplayText(option: ICascaderSelectOption, showChildrenCount: boolean): string | ReactNode {
  const optionText = getTextContent(option?.text);
  const childrenCount = option?.children?.length || 0;

  if (!showChildrenCount || !childrenCount || !optionText) {
    return option?.text;
  }

  return `${optionText} (${childrenCount})`;
}

export function hasOptionChildren(option: ICascaderSelectOption): boolean {
  return !!option?.children?.length;
}

export function formatLocaleOptions(
  formatMessage: TFormatMessage,
  options?: ICascaderSelectOption[] | null,
): ICascaderSelectOption[] {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  return options.map((option) => {
    const children = option?.children || [];
    const nextOption: ICascaderSelectOption = {
      ...option,
      text: formatLocaleText(formatMessage, option?.text) || option?.text,
      tooltip: formatLocaleText(formatMessage, option?.tooltip),
    };

    if (children.length > 0) {
      nextOption.children = formatLocaleOptions(formatMessage, children);
    }

    return nextOption;
  });
}

export function findOptionPath(options?: ICascaderSelectOption[] | null, value?: string): IFindOptionResult {
  if (!value) {
    return {
      path: [],
    };
  }

  return findOptionPathRecursive(options, value, []);
}

export function buildColumns(
  options?: ICascaderSelectOption[] | null,
  activePath?: string[] | null,
): ICascaderSelectOption[][] {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const columns = [options];

  if (!Array.isArray(activePath) || activePath.length === 0) {
    return columns;
  }

  let currentOptions = options;

  for (const value of activePath) {
    const currentOption = currentOptions.find((option) => option?.value === value);
    const children = currentOption?.children || [];

    if (!children.length) {
      break;
    }

    columns.push(children);
    currentOptions = children;
  }

  return columns;
}

export function getDefaultActivePath(options?: ICascaderSelectOption[] | null): string[] {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const path: string[] = [];
  let currentOptions = options;

  while (currentOptions.length > 0) {
    const firstEnabledOption = getFirstEnabledOption(currentOptions);
    const children = firstEnabledOption?.children || [];

    if (!firstEnabledOption || !children.length) {
      break;
    }

    path.push(firstEnabledOption.value);
    currentOptions = children;
  }

  return path;
}

export function getCascadedActivePath(path: ICascaderSelectOption[], children: ICascaderSelectOption[]): string[] {
  return [...getPathValues(path), ...getDefaultActivePath(children)];
}

export function getOptionPathFromColumns(
  columns?: ICascaderSelectOption[][] | null,
  activePath?: string[] | null,
  option?: ICascaderSelectOption | null,
  columnIndex?: number,
): ICascaderSelectOption[] {
  const path: ICascaderSelectOption[] = [];

  if (Array.isArray(columns) && columns.length > 0 && typeof columnIndex === 'number' && !Number.isNaN(columnIndex)) {
    for (let index = 0; index < columnIndex; index += 1) {
      const pathOption = columns?.[index]?.find((item) => item?.value === activePath?.[index]);

      if (pathOption) {
        path.push(pathOption);
      }
    }
  }

  if (!!option) {
    path.push(option);
  }

  return path;
}

export function getCascaderColumnTitle(params?: IGetColumnTitleParams | null): string | ReactNode {
  if (!params) {
    return '';
  }

  const { formatMessage, columnIndex, levelTitles, firstLevelTitle, secondLevelTitle, locale } = params;
  const title = levelTitles?.[columnIndex];

  if (title) {
    return formatLocaleText(formatMessage, title);
  }

  if (!levelTitles && columnIndex === 0) {
    return firstLevelTitle;
  }

  if (!levelTitles && columnIndex === 1) {
    return secondLevelTitle;
  }

  if (typeof locale === 'string' && locale.toLowerCase().startsWith('zh')) {
    return `第${columnIndex + 1}级分类`;
  }

  return `Level ${columnIndex + 1}`;
}

export function getFirstEnabledOption(options?: ICascaderSelectOption[] | null): ICascaderSelectOption | undefined {
  return options?.find((option) => !option?.disabled);
}

export function filterOptions(options?: ICascaderSelectOption[] | null, search?: string): ICascaderSelectOption[] {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const realSearch = search?.trim() || '';

  if (!realSearch) {
    return options;
  }

  return options.reduce<ICascaderSelectOption[]>((result, option) => {
    const isParentMatched = isMatchedOption(option, realSearch);
    const children = option?.children || [];

    if (isParentMatched) {
      result.push(option);
      return result;
    }

    const matchedChildren = filterOptions(children, realSearch);

    if (matchedChildren?.length > 0) {
      result.push({
        ...option,
        children: matchedChildren,
      });
    }

    return result;
  }, []);
}

export function getLeafChangeContext(
  option?: ICascaderSelectOption | null,
  path?: ICascaderSelectOption[] | null,
): ICascaderSelectChangeContext {
  if (!option || !Array.isArray(path) || path.length === 0) {
    return { selectedOption: undefined, parentOption: undefined, path: [] };
  }

  const parentOption = path[path.length - 2];
  let realPath = path;

  if (!realPath.length) {
    realPath = [option];
  }

  return {
    selectedOption: option,
    parentOption,
    path: realPath,
  };
}

export function getPathValues(path?: ICascaderSelectOption[] | null): string[] {
  if (!Array.isArray(path) || path.length === 0) {
    return [];
  }

  return path.filter((option) => !!option?.value).map((option) => option.value);
}

function findOptionPathRecursive(
  options?: ICascaderSelectOption[] | null,
  value?: string,
  ancestors?: ICascaderSelectOption[] | null,
): IFindOptionResult {
  if (!Array.isArray(options) || options.length === 0) {
    return { path: [] };
  }

  for (const option of options) {
    const path = [...(ancestors || []), option];

    if (option?.value === value) {
      return {
        option,
        parentOption: ancestors?.[ancestors?.length - 1],
        path,
      };
    }

    const children = option?.children || [];
    const childResult = findOptionPathRecursive(children, value, path);

    if (childResult?.option) {
      return childResult;
    }
  }

  return {
    path: [],
  };
}

function isMatchedOption(option?: ICascaderSelectOption | null, search?: string): boolean {
  const text = getTextContent(option?.text)?.toLowerCase() || '';

  return text?.includes(search?.toLowerCase() || '');
}
